import type { ReactNode } from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import { isEmpty } from "lodash";
import { useTrialBanner } from "@/react/components/Layout/TrialBanner/useTrialBanner";
import { useSmOrMdOrLgScreenMediaQuery } from "@/react/hooks/useMediaQuery";
import { useElectronAPI } from "@/react/hooks/utils/useElectronAPI";
import { classNames } from "@circle-react/helpers/twMergeWithCN";
import { useRailbar } from "@circle-react/hooks/chatsV2";
import { useDmsV3Enabled } from "@circle-react/hooks/useDmsV3Enabled";
import { useMasqueradeEnabledAndActive } from "@circle-react/hooks/useMasqueradeEnabledAndActive";
import { useLocalStorage } from "@circle-react/hooks/utils/useLocalStorage";
import type { SkeletonVariant } from "@circle-react-uikit/SkeletonLoader";
import { SkeletonLoader } from "@circle-react-uikit/SkeletonLoader";
import { Content } from "./Content";
import { Header } from "./Header";
import { RailbarDragger } from "./RailbarDragger";

export const HEADER_HEIGHT = 64;
export const HEADER_HEIGHT_SM = 48;
export const BOTTOM_NAV_HEIGHT = 64;
export const DEFAULT_RAILBAR_WIDTH = 360;

export interface RailProps {
  children?: ReactNode;
  className?: string;
  isBelowHeader?: boolean;
  isFullWidth?: boolean;
  isLoading?: boolean;
  loaderVariant?: SkeletonVariant;
  noBorderLeft?: boolean;
  shouldHideRailbarDragger?: boolean;
}

const RAILBAR_WIDTH_LOCAL_STORAGE_KEY = "circle-railbar-width";

const Rail = ({
  isBelowHeader = false,
  noBorderLeft = false,
  isLoading,
  loaderVariant = "rail-bar",
  children,
  className = "",
  shouldHideRailbarDragger = false,
  isFullWidth = false,
}: RailProps) => {
  const { isTrialBannerExists } = useTrialBanner();
  const isSmOrMdScreen = useSmOrMdOrLgScreenMediaQuery();
  const { isMessagingModalOpen } = useDmsV3Enabled();
  const { isLiveStream, isCourseSpace, isDrawer } = useRailbar();
  const isMasqueradingEnabledAndActive = useMasqueradeEnabledAndActive();
  const { isElectron } = useElectronAPI();

  const sidebarRef = useRef<HTMLInputElement>(null);
  const [isResizing, setIsResizing] = useState(false);
  const { setValue, getValue } = useLocalStorage<number>(
    RAILBAR_WIDTH_LOCAL_STORAGE_KEY,
  );
  const [railbarWidth, setRailbarWidth] = useState<number>(
    getValue() || DEFAULT_RAILBAR_WIDTH,
  );

  const startResizing = useCallback(() => {
    setIsResizing(true);
  }, []);

  const stopResizing = useCallback(() => {
    setIsResizing(false);
  }, []);

  const resize = useCallback(
    (mouseMoveEvent: MouseEvent) => {
      if (isResizing) {
        const screenWidth =
          sidebarRef.current?.getBoundingClientRect().right || 0;
        const sidebarWidth = Math.abs(mouseMoveEvent.clientX - screenWidth);
        if (sidebarWidth > 332 && sidebarWidth < 720) {
          setRailbarWidth(sidebarWidth);
          setValue(sidebarWidth);
        }
      }
    },
    [isResizing],
  );

  useEffect(() => {
    window.addEventListener("mousemove", resize);
    window.addEventListener("mouseup", stopResizing);
    return () => {
      window.removeEventListener("mousemove", resize);
      window.removeEventListener("mouseup", stopResizing);
    };
  }, [resize, stopResizing]);

  const isCourseMemberView = () => {
    const helloBar = document.getElementById("lesson-hello-bar");
    return isCourseSpace && isEmpty(helloBar);
  };

  const shouldHideRaibarDragOption =
    shouldHideRailbarDragger || isLiveStream || isCourseSpace;
  if (isLoading) {
    return <SkeletonLoader variant={loaderVariant} />;
  }

  const railbarWidthStyle = shouldHideRailbarDragger
    ? DEFAULT_RAILBAR_WIDTH
    : railbarWidth;
  return (
    <div
      className={classNames("flex h-full flex-row", className)}
      style={{ width: railbarWidthStyle }}
    >
      {!shouldHideRaibarDragOption && (
        <RailbarDragger
          startResizing={startResizing}
          isMessagingModalOpen={isMessagingModalOpen}
          isTrialBannerExists={isTrialBannerExists}
        />
      )}
      <div
        ref={sidebarRef}
        id="rail-content"
        className={classNames(
          "bg-primary border-primary fixed right-[inherit] top-0 z-20 h-full border-l opacity-100",
          {
            "md:top-16": !isMessagingModalOpen,
            "lg:!top-0":
              isMessagingModalOpen ||
              isLiveStream ||
              isDrawer ||
              isCourseMemberView(),
            "lg:top-28":
              (isTrialBannerExists && !isCourseSpace) ||
              isMasqueradingEnabledAndActive,
            "lg:top-40":
              isTrialBannerExists &&
              !isCourseSpace &&
              isMasqueradingEnabledAndActive,
            "lg:top-20": isBelowHeader,
            "border-l-0": noBorderLeft,
            "z-50 !h-full !w-full": isSmOrMdScreen,
            "absolute !h-[calc(100vh-32px)] rounded-lg": isLiveStream,
            "!w-96": isLiveStream && !isFullWidth,
            "!w-full": isDrawer || isFullWidth,
            "!top-[6.325rem]": isElectron,
          },
        )}
        style={{ width: railbarWidthStyle }}
      >
        {children}
      </div>
    </div>
  );
};

Rail.Header = Header;
Rail.Content = Content;

export { Rail };
